/**
 * Что роль может достать в API — и, следовательно, что кабинет ей показывает.
 *
 * Правило оболочки: **роль меняет состав экрана, а не доступность контролов**
 * (app-kit.md §1). Того, чего роль не может, на экране нет; серый контрол с
 * объяснением — это временное состояние (квота, идущий замер), никогда не
 * права. Значит решение «показывать или нет» принимается до отрисовки, а не по
 * ответу 403.
 *
 * Два источника, и они не спорят.
 *
 * 1. **`session.permissions`** — плоский список действий, который посчитал
 *    сервер. Это главный источник для конкретной кнопки: сервер знает не только
 *    роль, но и тариф, и состояние подписки. Фронт из роли права не выводит.
 * 2. **Матрица `x-seeno-access`** из контракта (`access-rules.ts`, порождается)
 *    — ей отвечает вопрос «дотянется ли эта роль до такого пути». Ею закрыты
 *    целые разделы навигации: у клиента агентства нет ядра, у наблюдателя нет
 *    участников. Матрица не сочинена фронтом — она взята из того же файла, из
 *    которого сервер её и применяет.
 *
 * Первое совпадение выигрывает, умолчание закрытое.
 */

import { ACCESS_RULES, type AccessRule, type Role } from './access-rules.ts';

export type { Role };
export { ROLES } from './access-rules.ts';

/** Человеческие названия ролей. Единственное место, где они пишутся. */
export const ROLE_NAMES: Record<Role, string> = {
  owner: 'Владелец',
  analyst: 'Аналитик',
  agency_client: 'Клиент агентства',
  viewer: 'Наблюдатель',
};

/**
 * Одна фраза о том, что роль делает. Показывается в переключателе контекста:
 * человек должен понимать, почему часть разделов ему не видна.
 */
export const ROLE_SUMMARY: Record<Role, string> = {
  owner: 'Полный доступ, включая оплату и участников',
  analyst: 'Бренды, ядро, замеры и отчёты. Без оплаты и участников',
  agency_client: 'Свой бренд, отчёты и рекомендации',
  viewer: 'Просмотр и выгрузка. Без изменений',
};

/** Роли, которые ничего не меняют: экран для них работает в режиме чтения. */
const READ_ONLY: ReadonlySet<Role> = new Set<Role>(['viewer', 'agency_client']);

export function isReadOnly(role: Role | null | undefined): boolean {
  return role ? READ_ONLY.has(role) : true;
}

/**
 * Сопоставление сегмента пути.
 *
 * `**` берёт остаток (один сегмент и больше), `{param}` — ровно один. Параметр
 * сравнивается как подстановка, а не по имени: правило написано с
 * `{brandId}`, спрашивать могут и шаблоном, и настоящим идентификатором.
 */
function matchPath(pattern: string, path: string): boolean {
  const p = pattern.split('/').filter(Boolean);
  const q = path.split('/').filter(Boolean);

  for (const [i, segment] of p.entries()) {
    if (segment === '**') return q.length > i;
    if (i >= q.length) return false;
    if (segment.startsWith('{') && segment.endsWith('}')) continue;
    if (segment !== q[i]) return false;
  }
  return p.length === q.length;
}

function matchMethod(rule: AccessRule, method: string): boolean {
  return rule.methods.includes('*') || rule.methods.includes(method.toUpperCase());
}

/**
 * Дотянется ли роль до пути контракта. `role = null` — неаутентифицированный
 * посетитель: ему доступно только то, что помечено `public`.
 *
 * Путь передаётся шаблоном контракта (`/brands/{brandId}/questions`), но
 * настоящий адрес тоже подойдёт: подстановки сравниваются позиционно.
 */
export function allows(role: Role | null, method: string, path: string): boolean {
  for (const rule of ACCESS_RULES) {
    if (!matchMethod(rule, method)) continue;
    if (!rule.paths.some((pattern) => matchPath(pattern, path))) continue;

    if (rule.roles === 'public') return true;
    if (rule.roles === 'any_member') return role !== null;
    return role !== null && rule.roles.includes(role);
  }
  // Умолчание закрытое: операция без правила недоступна никому.
  return false;
}

/**
 * Разрешено ли конкретное действие. Спрашивается у сервера — из списка,
 * который он посчитал сам.
 *
 * Названия действий контракт задаёт примерами (`brands.write`, `runs.start`,
 * `reports.write`), полного перечня в нём нет. Поэтому имя действия здесь —
 * ключ к серверному списку, а не самостоятельное правило: если сервер такого
 * действия не прислал, кнопки нет. Ошибка в имени прячет кнопку, а не открывает
 * её — направление отказа выбрано намеренно.
 */
export function granted(permissions: readonly string[], action: string): boolean {
  return permissions.includes(action);
}
